import { useId, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { useTradeShows } from "@/api/queries";
import { Button, Select } from "@/components/ui";

import type { GridColumn, GridRow } from "./model";
import {
  GRID_COLUMNS,
  MAX_LENGTH,
  REQUIRED_COLUMNS,
  editionYears,
  emptyRow,
  isBlank,
  parseClipboard,
  pasteBlock,
  quickCheck,
} from "./model";
import type { FieldErrors } from "./queries";
import { useBulkCards } from "./queries";

/**
 * *Captura en tabla*: many cards at once, the way a seller would fill a sheet.
 *
 * Every cell is text, so a block copied from Excel pastes straight in — the
 * expo by name, the city as "Monterrey, N.L." — and the server resolves both
 * as the import does. *Guardar* saves every row that is right; the rest stay
 * on screen with the reason on each cell, so only those need another look.
 */

const STARTING_ROWS = 10;

/** Cells whose content is a number, for the phone keyboard. */
const TEL_COLUMNS = new Set<GridColumn>(["phone", "mobile", "phone2"]);

export function CardGrid() {
  const { t } = useTranslation();
  const keys = useRef(0);
  const nextKey = () => ++keys.current;
  const [rows, setRows] = useState<GridRow[]>(() =>
    Array.from({ length: STARTING_ROWS }, () => emptyRow(nextKey())),
  );
  /** What the server said about each row, by row key, until its cell changes. */
  const [serverErrors, setServerErrors] = useState<Record<number, FieldErrors>>({});
  const [tradeShow, setTradeShow] = useState("");
  const [edition, setEdition] = useState("");
  const [outcome, setOutcome] = useState<{ saved: number; failed: number } | null>(
    null,
  );
  const [failure, setFailure] = useState<string | null>(null);
  const bulk = useBulkCards();
  const tradeShows = useTradeShows();
  const showsListId = useId();
  const table = useRef<HTMLTableElement>(null);

  const filled = rows.filter((row) => !isBlank(row));

  function focusCell(row: number, column: number) {
    table.current
      ?.querySelector<HTMLInputElement>(`[data-cell="${row}:${column}"]`)
      ?.focus();
  }

  function edit(index: number, column: GridColumn, value: string) {
    setOutcome(null);
    const key = rows[index]?.key;
    setRows((previous) =>
      previous.map((row, at) => (at === index ? { ...row, [column]: value } : row)),
    );
    if (key !== undefined && serverErrors[key]?.[column]) {
      setServerErrors((previous) => {
        const { [column]: _cleared, ...rest } = previous[key] ?? {};
        return { ...previous, [key]: rest };
      });
    }
  }

  function onKeyDown(
    event: KeyboardEvent<HTMLInputElement>,
    row: number,
    column: number,
  ) {
    if (event.key === "Enter" || event.key === "ArrowDown") {
      event.preventDefault();
      if (row === rows.length - 1) {
        setRows((previous) => [...previous, emptyRow(nextKey())]);
        // The new row renders on the next frame.
        requestAnimationFrame(() => focusCell(row + 1, column));
      } else {
        focusCell(row + 1, column);
      }
    } else if (event.key === "ArrowUp" && row > 0) {
      event.preventDefault();
      focusCell(row - 1, column);
    }
  }

  function onPaste(
    event: ClipboardEvent<HTMLInputElement>,
    row: number,
    column: number,
  ) {
    const text = event.clipboardData.getData("text/plain");
    // One value goes into one cell, as an ordinary paste would put it.
    if (!/[\t\n]/.test(text.replace(/\r?\n$/, ""))) return;
    event.preventDefault();
    setOutcome(null);
    setRows((previous) =>
      pasteBlock(previous, parseClipboard(text), { row, column }, nextKey),
    );
  }

  function removeRow(index: number) {
    setRows((previous) => {
      const next = previous.filter((_, at) => at !== index);
      return next.length > 0 ? next : [emptyRow(nextKey())];
    });
  }

  async function save() {
    setFailure(null);
    setOutcome(null);
    const sent = rows.filter((row) => !isBlank(row));
    if (sent.length === 0) return;
    try {
      const result = await bulk.mutateAsync({
        cards: sent.map((row) => {
          const cells: Record<string, string> = {};
          for (const column of GRID_COLUMNS) cells[column] = row[column].trim();
          return cells;
        }),
        trade_show: tradeShow ? Number(tradeShow) : null,
        edition: edition ? Number(edition) : null,
      });
      const refused = new Map(
        result.errors.map((error) => [sent[error.index]?.key, error.errors]),
      );
      const kept = sent.filter((row) => refused.has(row.key));
      setServerErrors(
        Object.fromEntries(kept.map((row) => [row.key, refused.get(row.key) ?? {}])),
      );
      // What was saved leaves the table; what was refused moves to the top,
      // with room below it to carry on.
      setRows([
        ...kept,
        ...Array.from({ length: Math.max(STARTING_ROWS - kept.length, 3) }, () =>
          emptyRow(nextKey()),
        ),
      ]);
      setOutcome({ saved: result.created.length, failed: kept.length });
    } catch (problem) {
      setFailure(problem instanceof Error ? problem.message : t("errors.saveCard"));
    }
  }

  const shows = tradeShows.data?.results ?? [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-mute-600">
          {t("cards.grid.defaultShow")}
          <Select
            className="mt-1 w-auto"
            value={tradeShow}
            onChange={(event) => setTradeShow(event.target.value)}
          >
            <option value="">{t("cards.form.noShow")}</option>
            {shows.map((show) => (
              <option key={show.id} value={String(show.id)}>
                {show.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="text-xs text-mute-600">
          {t("cards.grid.defaultEdition")}
          <Select
            className="mt-1 w-auto"
            value={edition}
            onChange={(event) => setEdition(event.target.value)}
          >
            <option value="">{t("cards.form.noEdition")}</option>
            {editionYears().map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </Select>
        </label>
        <p className="mb-0 max-w-[52ch] text-xs text-mute-600">
          {t("cards.grid.hint")}
        </p>
      </div>

      {outcome ? (
        <p
          role="status"
          className="mb-4 border-2 border-rule bg-panel px-3 py-2 text-sm"
        >
          {t("cards.grid.saved", { count: outcome.saved })}
          {outcome.failed > 0
            ? ` ${t("cards.grid.failed", { count: outcome.failed })}`
            : ""}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {failure}
        </p>
      ) : null}

      <datalist id={showsListId}>
        {shows.map((show) => (
          <option key={show.id} value={show.name} />
        ))}
      </datalist>

      <div className="overflow-x-auto border-2 border-rule">
        {/* A hairline on every side of every cell, Excel's grid, over the
            system table's row rules: it is what tells a seller each cell takes
            typing and pasting like a sheet's. */}
        <table
          ref={table}
          className="table min-w-max [&_td]:border [&_td]:border-mute-300 [&_th]:border [&_th]:border-mute-300"
        >
          <thead>
            <tr>
              <th className="w-10 text-center">#</th>
              {GRID_COLUMNS.map((column) => (
                <th key={column}>
                  {t(`cards.fields.${column}`)}
                  {REQUIRED_COLUMNS.includes(column) ? (
                    <span className="text-brand-700"> *</span>
                  ) : null}
                </th>
              ))}
              <th>
                <span className="sr-only">{t("cards.grid.remove")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const local = isBlank(row) ? {} : quickCheck(row);
              const remote = serverErrors[row.key] ?? {};
              const other = Object.entries(remote)
                .filter(([key]) => !GRID_COLUMNS.includes(key as GridColumn))
                .flatMap(([, messages]) => messages);
              return (
                <tr key={row.key}>
                  <td className="text-center align-top text-xs text-mute-600">
                    {index + 1}
                  </td>
                  {GRID_COLUMNS.map((column, columnIndex) => {
                    const error = remote[column]?.[0] ?? local[column]?.[0];
                    return (
                      <td key={column} className="p-0 align-top">
                        <input
                          data-cell={`${index}:${columnIndex}`}
                          className={`block w-full min-w-[150px] bg-transparent px-2 py-2 text-sm outline-none focus:bg-panel ${
                            error
                              ? "bg-brand-100 shadow-[inset_0_0_0_2px_var(--color-accent)]"
                              : ""
                          }`}
                          value={row[column]}
                          maxLength={MAX_LENGTH[column]}
                          inputMode={TEL_COLUMNS.has(column) ? "tel" : undefined}
                          list={column === "trade_show" ? showsListId : undefined}
                          aria-label={t("cards.grid.cell", {
                            column: t(`cards.fields.${column}`),
                            row: index + 1,
                          })}
                          aria-invalid={error ? true : undefined}
                          title={error}
                          onChange={(event) => edit(index, column, event.target.value)}
                          onKeyDown={(event) => onKeyDown(event, index, columnIndex)}
                          onPaste={(event) => onPaste(event, index, columnIndex)}
                        />
                        {error ? (
                          <span className="block px-2 pb-1 text-[11px] leading-tight text-brand-700">
                            {error}
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                  <td className="p-0 align-top">
                    <button
                      type="button"
                      className="px-3 py-2 text-mute-600 hover:text-ink"
                      aria-label={t("cards.grid.removeRow", { row: index + 1 })}
                      onClick={() => removeRow(index)}
                    >
                      ✕
                    </button>
                    {other.length > 0 ? (
                      <span className="block px-2 pb-1 text-[11px] text-brand-700">
                        {other.join(" ")}
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          disabled={bulk.isPending || filled.length === 0}
          onClick={() => void save()}
        >
          {bulk.isPending
            ? t("cards.form.saving")
            : t("cards.grid.save", { count: filled.length })}
        </Button>
        <Button
          onClick={() =>
            setRows((previous) => [
              ...previous,
              ...Array.from({ length: STARTING_ROWS }, () => emptyRow(nextKey())),
            ])
          }
        >
          {t("cards.grid.addRows")}
        </Button>
      </div>
    </div>
  );
}
